/**
 * Loads an express cart page as soon as it opens, with the checkout function's
 * POST /link (nile-ai-mastra: supabase/functions/checkout/checkout-endpoints.md):
 * the link's items (which stay on the page) and the current session's cart
 * (whose other items can be removed; each row carries its product's variants).
 * The brand's theme comes from retailerData on POST /link, whether or not the
 * shopper already has a session.
 */

import { ApiError, expressCartApi } from "./client";
import type { CartSession } from "@/hooks/useCartSession";
import type { CheckoutLink } from "@/lib/express-cart/checkout";
import { productFromCartRows, sessionCartProducts } from "@/lib/express-cart/session-cart";
import { brandLinkChatUrl, sessionChatUrl } from "@/lib/express-cart/link-source";
import { clearExpressSession, readExpressSession } from "@/lib/express-cart/device";
import type { CheckoutProduct } from "@/lib/express-cart/variants";
import { brandThemeFromRetailerData, type BrandTheme } from "@/lib/brand-theme";
import type { CartItem, CheckoutLinkProduct, CheckoutLinkResponse } from "@/types";

export interface ExpressCartData {
  brand: BrandTheme;
  link: CheckoutLink;
  /** The session's cart rows the page starts from. */
  items: CartItem[];
  session: CartSession | null;
  /** e.g. "shopify", "shopify_public", "magento"; null when unknown. */
  integrationType: string | null;
  /** The brand's WhatsApp chat (wa.me/{number}), when known; no pre-filled text. */
  brandChatUrl: string | null;
}

/**
 * The WhatsApp button's link. With a session, it opens the brand's chat with
 * the cart question pre-filled, so the agent can attach the shopper's WhatsApp
 * number to this session; a brand link without one uses the link's own chat.
 */
function chatButtonUrl(brandChatUrl: string | null, session: CartSession | null, slug: string): string | null {
  if (session && brandChatUrl) return sessionChatUrl(brandChatUrl, session.session_id);
  return slug ? brandLinkChatUrl(slug) : null;
}

function isCartRow(item: CheckoutLinkProduct | CartItem): item is CartItem {
  return "product_id" in item;
}

function toCheckoutProduct(product: CheckoutLinkProduct | CheckoutProduct): CheckoutProduct {
  return {
    ...(product as CheckoutProduct),
    sku: (product as CheckoutLinkProduct).sku ?? (product as CheckoutProduct).sku ?? null,
    variants: (product.variants ?? []) as CheckoutProduct["variants"],
    currency: product.currency ?? "ZAR",
  };
}

/** Turns a /link response into the page's products, starting rows and pinned products. */
function buildCart(
  response: CheckoutLinkResponse,
  brand: BrandTheme,
  integrationType: string | null,
  session: CartSession | null,
  slug: string,
  brandChatUrl: string | null,
): ExpressCartData {
  const retailerId = brand.id;
  const linkItems = response.linkItems ?? [];
  const linkProducts = linkItems.filter((i): i is CheckoutLinkProduct => !isCartRow(i));
  const rows = [...linkItems.filter(isCartRow), ...(response.cartItems ?? [])];
  const linkProductIds = [...new Set(linkItems.map((i) => (isCartRow(i) ? i.product_id : i.id)))];

  // Link products not in the cart come with their data; products behind cart
  // rows are built from the rows, which carry their variants (get_cart).
  const byId = new Map(linkProducts.map(toCheckoutProduct).map((p) => [p.id, p]));
  for (const id of new Set(rows.map((r) => r.product_id))) {
    if (byId.has(id)) continue;
    const product = productFromCartRows(rows.filter((r) => r.product_id === id));
    if (product) byId.set(id, product);
  }

  return {
    brand,
    link: {
      id: slug || (session?.session_id ?? ""),
      code: slug,
      name: null,
      retailer_id: retailerId,
      retailer_name: brand.name,
      whatsapp_url: chatButtonUrl(brandChatUrl, session, slug),
      // Cart items first, then the link's other products.
      products: sessionCartProducts([...byId.values()], rows, linkProductIds),
      // The link's items stay on the page; other cart items can be removed.
      pinnedProductIds: linkProductIds,
    },
    items: rows,
    session,
    integrationType,
    brandChatUrl,
  };
}

/**
 * A brand link (/cart/:slug). Its slug-specific session is sent with the
 * initial link request so this load only needs one POST /link.
 */
export async function loadLinkCart(slug: string): Promise<ExpressCartData> {
  const stored = readExpressSession(slug);
  let response: CheckoutLinkResponse;
  let session: CartSession | null = null;
  let contact: { whatsappUrl: string } | null = null;

  if (stored) {
    session = { session_id: stored.session_id, session_token: stored.session_token };
    try {
      [response, contact] = await Promise.all([
        expressCartApi.getLink({ slug }, stored.session_token),
        expressCartApi.getWhatsAppContact(stored.session_token),
      ]);
    } catch (error) {
      // An expired or revoked token: forget it and show the link on its own.
      if (!(error instanceof ApiError && error.statusCode === 401)) throw error;
      clearExpressSession(slug);
      session = null;
      response = await expressCartApi.getLink({ slug });
    }
  } else {
    response = await expressCartApi.getLink({ slug });
  }

  if (!response.retailerId) throw new ApiError("Link not found", "Link not found", 404);
  const brand = brandThemeFromRetailerData(response.retailerId, response.retailerData);
  const integrationType = response.integrationType?.toLowerCase() ?? null;
  return buildCart(response, brand, integrationType, session, slug, contact?.whatsappUrl ?? null);
}

/**
 * The agent's View Cart link (/cart?session={sessionId}&token={token}): the
 * session's cart, plus the items of the link the session came from, if any.
 */
export async function loadSessionCart(session: CartSession): Promise<ExpressCartData> {
  const [response, contact] = await Promise.all([
    expressCartApi.getLink({}, session.session_token),
    expressCartApi.getWhatsAppContact(session.session_token),
  ]);
  const retailerId = response.retailerId;
  if (!retailerId) throw new ApiError("Missing retailer ID", "The cart response is missing its retailer.", 500);
  const brand = brandThemeFromRetailerData(retailerId, response.retailerData);
  const integrationType = response.integrationType?.toLowerCase() ?? null;
  return buildCart(response, brand, integrationType, session, "", contact?.whatsappUrl ?? null);
}
